/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import { useCallback, useState } from "react"

const readStoredSize = (storageKey: string): boolean => {
  try {
    return window.localStorage.getItem(storageKey) === "large"
  } catch {
    return false
  }
}

/**
 * The small-A / large-A interface size preference of a review surface:
 * whether the larger size is on, and the toggle. Remembered per surface under
 * `storageKey` ("large" / "normal"); where storage is unavailable the choice
 * simply lasts for the visit. Toggling fires a window resize so that canvases
 * and other measured layout re-measure at the new size.
 */
const useInterfaceSize = (storageKey: string): [boolean, () => void] => {
  const [large, setLarge] = useState(() => readStoredSize(storageKey))
  const toggle = useCallback(() => {
    setLarge((current) => {
      const next = !current
      try {
        window.localStorage.setItem(storageKey, next ? "large" : "normal")
      } catch {
        // Not remembered; the toggle still applies
      }
      return next
    })
    window.dispatchEvent(new Event("resize"))
  }, [storageKey])
  return [large, toggle]
}

export default useInterfaceSize
